import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import {
  CalculatePerDiemDocument,
  CalculatePerDiemMutation,
  CalculatePerDiemMutationVariables,
} from 'generated/resources/graphql'

import { PerDiemDay } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import { Expensable } from 'modules/expenses/types/Expensable'

interface UsePerDiemBreakdownParams {
  expensable?: Expensable
  employeeId?: string
}

interface UsePerDiemBreakdownResult {
  days: Array<PerDiemDay | null> | null
  isLoading: boolean
  totalAmountCents: number
  currency: string
}

export const usePerDiemBreakdown = ({
  expensable,
  employeeId,
}: UsePerDiemBreakdownParams): UsePerDiemBreakdownResult => {
  const perDiem = expensable?.perDiem
  const concepts = useMemo(() => perDiem?.perDiemConcepts || [], [perDiem?.perDiemConcepts])
  const destinations = useMemo(
    () => perDiem?.perDiemDestinations || [],
    [perDiem?.perDiemDestinations]
  )

  const currency = expensable?.currency || perDiem?.currency || 'EUR'

  const totalAmountCents = Number(expensable?.amount || 0)

  const [days, setDays] = useState<Array<PerDiemDay | null> | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [calculateMutation] = useMutation<
    CalculatePerDiemMutation,
    CalculatePerDiemMutationVariables
  >(CalculatePerDiemDocument)

  const fetchBreakdown = useCallback(async () => {
    if (!perDiem?.startDate || !destinations.length || !employeeId) {
      return
    }

    setIsLoading(true)

    try {
      // Map destinations to the format expected by the mutation
      const mappedDestinations = destinations.map((dest) => ({
        arrivalDatetime: dest.arrivalDate,
        locationData: {
          country: dest.country || '',
          administrativeAreaLevel1: dest.administrativeAreaLevel1,
          administrativeAreaLevel2: dest.administrativeAreaLevel2,
          locality: dest.locality,
        },
      }))

      // Map concepts to day exclusions
      const dayExclusions = concepts
        .map((concept) => {
          const excludedConcepts: Array<'breakfast' | 'lunch' | 'dinner' | 'accommodation'> = []
          if (concept.breakfastProvided) excludedConcepts.push('breakfast')
          if (concept.lunchProvided) excludedConcepts.push('lunch')
          if (concept.dinnerProvided) excludedConcepts.push('dinner')
          if (concept.accommodationProvided) excludedConcepts.push('accommodation')

          // Format date to YYYY-MM-DD
          const date = new Date(concept.date)
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(date.getDate()).padStart(2, '0')}`

          return {
            date: dateStr,
            excludedConcepts,
          }
        })
        .filter((e) => e.excludedConcepts.length > 0)

      const result = await calculateMutation({
        variables: {
          employeeId,
          startDate: perDiem.startDate,
          endDate: perDiem.endDate,
          from: perDiem.from || undefined,
          destinations: mappedDestinations,
          dayExclusions: dayExclusions.length > 0 ? dayExclusions : undefined,
        },
      })

      const calculatedDays = result.data?.expensesMutations?.calculateExpensesPerDiem?.perDiem?.days
      if (calculatedDays) {
        setDays(calculatedDays)
      }
    } catch {
      // Silently fail - breakdown will remain empty
    } finally {
      setIsLoading(false)
    }
  }, [perDiem, destinations, concepts, employeeId, calculateMutation])

  useEffect(() => {
    fetchBreakdown()
  }, [fetchBreakdown])

  return {
    days,
    isLoading,
    totalAmountCents,
    currency,
  }
}
